/**
 * The person search on the investigate page: type a name, mail or F&O alias,
 * pick one of at most ten candidates, and the page navigates to that person's
 * view. Debounced at 300 ms and silent below two characters, so a keystroke is
 * never a directory read.
 *
 * The candidates come from the identity map (alias → Entra → mail), which by
 * construction holds only person-attributable aliases: shared shop-floor and
 * system accounts never appear here (concept §3, §11).
 *
 * Built on the DS `Autocomplete` in `mode="none"`: the backend already did the
 * matching, so the component must not filter the ten it was given a second time
 * against whatever the user typed since.
 */

import { Icon, Search } from "@norican/icons";
import { Autocomplete, Typography } from "@norican/react";
import { useEffect, useState } from "react";

import { type FoConsoleCandidate, useFoConsoleUserSearch } from "@/lib/api";

import { apiErrorDetail, apiErrorStatus } from "./console-format";

/** Debounce between the last keystroke and the search request. */
const DEBOUNCE_MS = 300;
/** Below this many characters nothing is asked. */
const MIN_QUERY_LENGTH = 2;

interface UserSearchProps {
  /** Chosen a candidate — the page navigates. */
  onPick: (candidate: FoConsoleCandidate) => void;
  /** Locked until a reason exists. */
  disabled?: boolean;
}

export function UserSearch({ onPick, disabled = false }: UserSearchProps) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  const search = useFoConsoleUserSearch(disabled ? "" : debounced);
  const candidates = search.data ?? [];
  const tooShort = query.trim().length < MIN_QUERY_LENGTH;
  // A 403 is "not on the investigator list" — a definite answer, shown in place
  // of the candidates rather than as an empty "no person matches".
  const forbidden = apiErrorStatus(search.error) === 403;

  return (
    <div className="flex flex-col gap-1">
      <Autocomplete.Root<FoConsoleCandidate>
        items={candidates}
        mode="none"
        value={query}
        onValueChange={(next) => setQuery(next)}
        itemToStringValue={(item) => item.mail}
        disabled={disabled}
      >
        <Autocomplete.Control>
          <Autocomplete.Input
            aria-label="Search a person by name, mail or F&O alias"
            placeholder={
              disabled
                ? "Enter a reason first"
                : "Name, mail or F&O alias — at least two characters"
            }
            autoComplete="off"
            spellCheck={false}
          />
          <Autocomplete.Clear aria-label="Clear search" />
        </Autocomplete.Control>
        <Autocomplete.Portal>
          <Autocomplete.Positioner sideOffset={4}>
            <Autocomplete.Popup aria-label="Matching people">
              {tooShort ? (
                <Autocomplete.Empty>
                  Keep typing — two characters start a search.
                </Autocomplete.Empty>
              ) : forbidden ? (
                <Autocomplete.Empty>
                  {apiErrorDetail(search.error) ??
                    "Not permitted: this sign-in is not on the investigator list."}
                </Autocomplete.Empty>
              ) : search.isFetching && candidates.length === 0 ? (
                <Autocomplete.Empty>Searching the identity map…</Autocomplete.Empty>
              ) : candidates.length === 0 ? (
                <Autocomplete.Empty>
                  No person matches. Shared shop-floor and system accounts are never listed.
                </Autocomplete.Empty>
              ) : null}
              <Autocomplete.List>
                {(item: FoConsoleCandidate) => (
                  <Autocomplete.Item key={item.mail} value={item} onClick={() => onPick(item)}>
                    <span className="flex flex-col">
                      <span className="text-fg text-sm font-medium">{item.display_name}</span>
                      <span className="text-fg-muted text-xs">
                        {item.mail} · {item.alias}
                      </span>
                    </span>
                  </Autocomplete.Item>
                )}
              </Autocomplete.List>
            </Autocomplete.Popup>
          </Autocomplete.Positioner>
        </Autocomplete.Portal>
      </Autocomplete.Root>
      <Typography variant="caption" tone="muted">
        <span className="inline-flex items-center gap-1">
          <Icon icon={Search} size="xs" aria-hidden="true" />
          Up to ten candidates from the identity map; picking one opens their lookback and writes an
          audit line.
        </span>
      </Typography>
    </div>
  );
}
